import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMaintenanceMode } from "../context/MaintenanceContext";
import {
  Skeleton,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  useTheme,
  InputAdornment,
  TextField,
  Card
} from "@mui/material";

import MaintenancePopup from "./MaintenancePopup";
import SearchIcon from "@mui/icons-material/Search";

const ReportProjectSelection = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [organization, setOrganization] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState("");
  const theme = useTheme();
  const { isPageInMaintenance } = useMaintenanceMode();

  const navigate = useNavigate();
  const organizationId = localStorage.getItem("organization_id");
  const token = localStorage.getItem("access_token");

  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects?organization_id=${organizationId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        const list = data.projects_list || [];
        setProjects(list);
        setFilteredProjects(list);
        setOrganization(data.organization_name);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching projects:", error);
        setIsLoading(false);
      });
  }, []);

  const handleProjectClick = async (project) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects/run-details?project_id=${project.project_id}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch project health details");
      }

      const data = await response.json();

      if (Array.isArray(data) && data.length > 0) {
        const sortedRunDetails = data.sort((a, b) => b.createdAt - a.createdAt);
        const latestRunId = sortedRunDetails[0]?.run_id;

        if (latestRunId) {
          navigate(`/rundetails/reporting/${latestRunId}`);
        } else {
          console.warn("Latest run_id is empty or missing");
        }
      } else {
        console.warn("Empty or invalid response data");
      }
    } catch (error) {
      console.error("Error in handleProjectClick:", error);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = projects.filter((project) =>
      project.name.toLowerCase().includes(value)
    );
    setFilteredProjects(filtered);
    setPage(0);
  };

  const dataToShow = searchTerm ? filteredProjects : projects;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: theme.palette.mode === "dark" ? "#282828" : "background.default",
        color: "text.primary",
        p: 0,
        m: 0,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          filter: isPageInMaintenance('reporting') ? 'blur(4px)' : 'none',
          transition: 'filter 0.3s ease',
          pointerEvents: isPageInMaintenance('reporting') ? 'none' : 'auto',
        }}
      >
        <Box sx={{ ml: 2, mr: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <Typography variant="h5" component="h2" sx={{ mt: 2, ml: 2 }}>
              {organization} Projects{" "}
              <Box component="span" sx={{ color: "gray", fontSize: "1.3rem" }}>
                (Reporting)
              </Box>
            </Typography>
            <TextField
              variant="outlined"
              placeholder="Search Projects"
              value={searchTerm}
              onChange={handleSearch}
              size="small"
              sx={{ mt: 2, mr: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {isLoading ? (
            <Box sx={{ mt: 2 }}>
              <Skeleton variant="text" width="60%" height={30} />
              <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
            </Box>
          ) : dataToShow.length === 0 ? (
            <Typography variant="body1" align="center" sx={{ marginTop: "20px" }}>
              No matching projects found.
            </Typography>
          ) : (
            <Box sx={{ pb: 2 }}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                          backgroundColor: theme.palette.mode === 'dark' ? '#202020' : 'rgb(12, 20, 50)',
                          '& .MuiTableCell-root': {
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            borderBottom: 'none',
                          }
                        }}
                    >
                      <TableCell>Project Name</TableCell>
                      <TableCell align="right">Description</TableCell>
                      <TableCell align="right">Last Updated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataToShow
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((project) => (
                        <TableRow
                          key={project.project_id}
                          onClick={() => handleProjectClick(project)}
                          sx={{
                              cursor: "pointer",
                              backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#fff',
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' ? '#333333' : '#f5f5f5',
                              },
                              '& .MuiTableCell-root': {
                                borderBottom: theme.palette.mode === 'dark' ? '1px solid #1a1a1a' : '1px solid rgba(224, 224, 224, 1)',
                              }
                            }}
                        >
                          <TableCell component="th" scope="row">
                            {project.name}
                          </TableCell>
                          <TableCell align="right">{project.description}</TableCell>
                          <TableCell align="right">
                            {new Date(parseInt(project.createdAt) * 1000).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 15, 20]}
                  component="div"
                  count={dataToShow.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </TableContainer>
            </Box>
          )}
        </Box>
      </Box>

      <MaintenancePopup open={isPageInMaintenance('reporting')} />

    </Box>
  );
};

export default ReportProjectSelection;